import { __assign, __extends, __rest } from "tslib";
import React from 'react';
import { compareArrayValues, compareDataFrameStructures, FieldMatcherID, fieldMatchers, FieldType, } from '@grafana/data';
import { withTheme } from '../../themes';
import { GraphNGContext } from './hooks';
import { preparePlotConfigBuilder, preparePlotFrame } from './utils';
import { preparePlotData } from '../uPlot/utils';
import { PlotLegend } from '../uPlot/PlotLegend';
import { UPlotChart } from '../uPlot/Plot';
import { LegendDisplayMode } from '../VizLegend/types';
import { VizLayout } from '../VizLayout/VizLayout';
export var FIXED_UNIT = '__fixed';
var UnthemedGraphNG = /** @class */ (function (_super) {
    __extends(UnthemedGraphNG, _super);
    function UnthemedGraphNG(props) {
        var _this = _super.call(this, props) || this;
        _this.mapSeriesIndexToDataFrameFieldIndex = function (i) {
            return _this.state.seriesToDataFrameFieldIndexMap[i];
        };
        _this.getTimeRange = function () {
            return _this.props.timeRange;
        };
        _this.getTimeZone = function () {
            return _this.props.timeZone;
        };
        var dimFields = props.fields;
        if (!dimFields) {
            dimFields = {
                x: fieldMatchers.get(FieldMatcherID.firstTimeField).get({}),
                y: fieldMatchers.get(FieldMatcherID.numeric).get({}),
            };
        }
        _this.state = { dimFields: dimFields };
        return _this;
    }
    /**
     * Since no matter the nature of the change (data vs config only) we always calculate the plot-ready AlignedData array.
     * It's cheaper than run prev and current AlignedData comparison to indicate necessity of data-only update. We assume
     * that if there were no config updates, we can do data only updates(as described in Plot.tsx, L32)
     *
     * Preparing the uPlot-ready data in getDerivedStateFromProps makes the data updates happen only once for a render cycle.
     * If we did it in componendDidUpdate we will end up having two data-only updates: 1) for props and 2) for state update
     *
     * This is a way of optimizing the uPlot rendering, yet there are consequences: when there is a config update,
     * the data is updated first, and then the uPlot is re-initialized. But since the config updates does not happen that
     * often (apart from the edit mode interactions) this should be a fair performance compromise.
     */
    UnthemedGraphNG.getDerivedStateFromProps = function (props, state) {
        var dimFields = props.fields;
        if (!dimFields) {
            dimFields = {
                x: fieldMatchers.get(FieldMatcherID.firstTimeField).get({}),
                y: fieldMatchers.get(FieldMatcherID.numeric).get({}),
            };
        }
        var frame = preparePlotFrame(props.data, dimFields);
        if (!frame) {
            return __assign(__assign({}, state), { dimFields: dimFields });
        }
        return __assign(__assign({}, state), { data: preparePlotData(frame, [FieldType.string]), alignedDataFrame: frame, seriesToDataFrameFieldIndexMap: frame.fields.map(function (f) { return f.state.origin; }), dimFields: dimFields });
    };
    UnthemedGraphNG.prototype.componentDidMount = function () {
        var theme = this.props.theme;
        // alignedDataFrame is already prepared by getDerivedStateFromProps method
        var alignedDataFrame = this.state.alignedDataFrame;
        if (!alignedDataFrame) {
            return;
        }
        this.setState({
            config: preparePlotConfigBuilder(alignedDataFrame, theme, this.getTimeRange, this.getTimeZone),
        });
    };
    UnthemedGraphNG.prototype.componentDidUpdate = function (prevProps) {
        var _a = this.props, data = _a.data, theme = _a.theme;
        var alignedDataFrame = this.state.alignedDataFrame;
        var shouldConfigUpdate = false;
        var stateUpdate = {};
        if (this.state.config === undefined || this.props.timeZone !== prevProps.timeZone) {
            shouldConfigUpdate = true;
        }
        if (data !== prevProps.data) {
            if (!alignedDataFrame) {
                return;
            }
            var hasStructureChanged = !compareArrayValues(data, prevProps.data, compareDataFrameStructures);
            if (shouldConfigUpdate || hasStructureChanged) {
                var builder = preparePlotConfigBuilder(alignedDataFrame, theme, this.getTimeRange, this.getTimeZone);
                stateUpdate = __assign(__assign({}, stateUpdate), { config: builder });
            }
        }
        if (Object.keys(stateUpdate).length > 0) {
            this.setState(stateUpdate);
        }
    };
    UnthemedGraphNG.prototype.renderLegend = function () {
        var _a = this.props, legend = _a.legend, onSeriesColorChange = _a.onSeriesColorChange, onLegendClick = _a.onLegendClick, data = _a.data;
        var config = this.state.config;
        if (!config || (legend && legend.displayMode === LegendDisplayMode.Hidden)) {
            return;
        }
        return (React.createElement(PlotLegend, __assign({ data: data, config: config, onSeriesColorChange: onSeriesColorChange, onLegendClick: onLegendClick, maxHeight: "35%", maxWidth: "60%" }, legend)));
    };
    UnthemedGraphNG.prototype.render = function () {
        var _this = this;
        var _a = this.props, width = _a.width, height = _a.height, children = _a.children, timeZone = _a.timeZone, timeRange = _a.timeRange, plotProps = __rest(_a, ["width", "height", "children", "timeZone", "timeRange"]);
        if (!this.state.data || !this.state.config) {
            return null;
        }
        return (React.createElement(GraphNGContext.Provider, { value: {
                mapSeriesIndexToDataFrameFieldIndex: this.mapSeriesIndexToDataFrameFieldIndex,
                dimFields: this.state.dimFields,
                data: this.state.alignedDataFrame,
            } },
            React.createElement(VizLayout, { width: width, height: height, legend: this.renderLegend() }, function (vizWidth, vizHeight) { return (React.createElement(UPlotChart, __assign({}, plotProps, { config: _this.state.config, data: _this.state.data, width: vizWidth, height: vizHeight, timeRange: timeRange }), children)); })));
    };
    return UnthemedGraphNG;
}(React.Component));
export var GraphNG = withTheme(UnthemedGraphNG);
GraphNG.displayName = 'GraphNG';
//# sourceMappingURL=GraphNG.js.map